import React from 'react';
import { ProjectStatus } from '../../types';
import { useI18n } from '../../context/I18nContext';
import Tooltip from '../ui/Tooltip';
import { Archive, FileText, Cog, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { KANBAN_ACTIVE_STATUSES } from './kanbanLayout';

export { KANBAN_ACTIVE_STATUSES };

function statusIcon(status: ProjectStatus) {
  const icons: Record<ProjectStatus, React.ReactNode> = {
    BRIEF_RECEIVED: <FileText className="w-4 h-4" />,
    IN_PRODUCTION: <Cog className="w-4 h-4" />,
    V1_SENT: <Send className="w-4 h-4" />,
    CHANGES_REQUESTED: <AlertCircle className="w-4 h-4" />,
    DELIVERED: <CheckCircle2 className="w-4 h-4" />,
    ARCHIVED: <Archive className="w-4 h-4" />,
  };
  return icons[status] || <FileText className="w-4 h-4" />;
}

function statusTextColor(status: ProjectStatus) {
  const colors: Record<ProjectStatus, string> = {
    BRIEF_RECEIVED: 'text-blue-700',
    IN_PRODUCTION: 'text-violet-700',
    V1_SENT: 'text-amber-700',
    CHANGES_REQUESTED: 'text-orange-700',
    DELIVERED: 'text-emerald-700',
    ARCHIVED: 'text-slate-700',
  };
  return colors[status] || 'text-gray-900';
}

function chipActiveClass(status: ProjectStatus) {
  const colors: Record<ProjectStatus, string> = {
    BRIEF_RECEIVED: 'bg-blue-600 text-white border-blue-600',
    IN_PRODUCTION: 'bg-violet-600 text-white border-violet-600',
    V1_SENT: 'bg-amber-600 text-white border-amber-600',
    CHANGES_REQUESTED: 'bg-orange-600 text-white border-orange-600',
    DELIVERED: 'bg-emerald-600 text-white border-emerald-600',
    ARCHIVED: 'bg-slate-600 text-white border-slate-600',
  };
  return colors[status] || 'bg-gray-800 text-white border-gray-800';
}

/** Soft tint for statuses currently shown in the compact column window (not the focus). */
function chipSoftClass(status: ProjectStatus) {
  const colors: Record<ProjectStatus, string> = {
    BRIEF_RECEIVED: 'border-blue-300 bg-blue-50 text-blue-800',
    IN_PRODUCTION: 'border-violet-300 bg-violet-50 text-violet-800',
    V1_SENT: 'border-amber-300 bg-amber-50 text-amber-800',
    CHANGES_REQUESTED: 'border-orange-300 bg-orange-50 text-orange-800',
    DELIVERED: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    ARCHIVED: 'border-slate-300 bg-slate-50 text-slate-800',
  };
  return colors[status] || 'border-gray-300 bg-gray-50 text-gray-800';
}

function chipSurfaceClass(
  status: ProjectStatus,
  options: { active: boolean; inWindow: boolean }
): string {
  if (options.active) return chipActiveClass(status);
  if (options.inWindow) return chipSoftClass(status);
  return 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50';
}

function statusLabel(status: ProjectStatus, t: (key: string) => string): string {
  const keys: Record<ProjectStatus, string> = {
    BRIEF_RECEIVED: 'projects.statusBriefReceived',
    IN_PRODUCTION: 'projects.statusInProduction',
    V1_SENT: 'projects.statusFirstVersion',
    CHANGES_REQUESTED: 'projects.statusCorrections',
    DELIVERED: 'projects.statusDelivered',
    ARCHIVED: 'projects.statusArchived',
  };
  return t(keys[status]);
}

function statusShortLabel(status: ProjectStatus, t: (key: string) => string): string {
  const keys: Record<ProjectStatus, string> = {
    BRIEF_RECEIVED: 'projects.statusBriefShort',
    IN_PRODUCTION: 'projects.statusProductionShort',
    V1_SENT: 'projects.statusV1Short',
    CHANGES_REQUESTED: 'projects.statusCorrectionsShort',
    DELIVERED: 'projects.statusDeliveredShort',
    ARCHIVED: 'projects.statusArchivedShort',
  };
  return t(keys[status]);
}

export interface KanbanCompactNavProps {
  counts: Partial<Record<ProjectStatus, number>>;
  archivedCount: number;
  /** Which chip looks selected (full color) */
  selected: ProjectStatus | 'ARCHIVED';
  onSelectStatus: (status: ProjectStatus) => void;
  onSelectArchived: () => void;
  /** Statuses currently shown as columns — soft tint on chips */
  visibleStatuses?: ProjectStatus[];
  /** Show short status text beside icon (when strip has room). */
  showLabels?: boolean;
  className?: string;
}

/**
 * Icon+count status strip used on compact kanban and archived mobile/tablet view.
 */
const KanbanCompactNav: React.FC<KanbanCompactNavProps> = ({
  counts,
  archivedCount,
  selected,
  onSelectStatus,
  onSelectArchived,
  visibleStatuses = [],
  showLabels = false,
  className = '',
}) => {
  const { t } = useI18n();
  const visibleSet = new Set(visibleStatuses);

  return (
    <div
      className={`flex w-full items-center gap-1 ${className}`}
      role="tablist"
      aria-label={t('projects.title')}
    >
      {KANBAN_ACTIVE_STATUSES.map((status) => {
        const count = counts[status] ?? 0;
        const active = selected === status;
        const inWindow = !active && visibleSet.has(status);
        const fullLabel = statusLabel(status, t);
        return (
          <Tooltip
            key={status}
            content={fullLabel}
            delay={showLabels ? 700 : 350}
            wrapperClassName="flex min-w-0 flex-1"
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={`${fullLabel} (${count})`}
              onClick={() => onSelectStatus(status)}
              className={`relative inline-flex w-full flex-row items-center justify-center gap-1 rounded-xl border px-1 py-1.5 transition-colors ${chipSurfaceClass(
                status,
                { active, inWindow }
              )}`}
            >
              <span className={`flex-shrink-0 ${active ? 'text-white/90' : statusTextColor(status)}`}>
                {statusIcon(status)}
              </span>
              {showLabels ? (
                <span
                  className={`min-w-0 truncate text-[10px] font-semibold leading-none ${
                    active ? 'text-white/90' : statusTextColor(status)
                  }`}
                >
                  {statusShortLabel(status, t)}
                </span>
              ) : null}
              <span
                className={`flex-shrink-0 text-[10px] font-semibold tabular-nums leading-none ${
                  active ? 'text-white/90' : statusTextColor(status)
                }`}
              >
                {count}
              </span>
            </button>
          </Tooltip>
        );
      })}
      <Tooltip
        content={t('projects.statusArchived')}
        delay={showLabels ? 700 : 350}
        wrapperClassName="flex min-w-0 flex-1"
      >
        <button
          type="button"
          role="tab"
          aria-selected={selected === 'ARCHIVED'}
          aria-label={`${t('projects.statusArchived')} (${archivedCount})`}
          onClick={onSelectArchived}
          className={`relative inline-flex w-full flex-row items-center justify-center gap-1 rounded-xl border px-1 py-1.5 transition-colors ${chipSurfaceClass(
            'ARCHIVED',
            { active: selected === 'ARCHIVED', inWindow: false }
          )}`}
        >
          <Archive
            className={`h-4 w-4 flex-shrink-0 ${selected === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'}`}
          />
          {showLabels ? (
            <span
              className={`min-w-0 truncate text-[10px] font-semibold leading-none ${
                selected === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'
              }`}
            >
              {t('projects.statusArchivedShort')}
            </span>
          ) : null}
          <span
            className={`flex-shrink-0 text-[10px] font-semibold tabular-nums leading-none ${
              selected === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'
            }`}
          >
            {archivedCount}
          </span>
        </button>
      </Tooltip>
    </div>
  );
};

export default KanbanCompactNav;
